import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

// Images of any aspect ratio fit the transparent viewer at 100%.
// At higher zoom levels, pointer drag pans within the image bounds.
export default function ArtworkViewer({ src, alt = '', controls = true }) {
  const { t } = useTranslation('dashboard');
  const viewportRef = useRef(null);
  const dragRef = useRef(null);
  const [viewport, setViewport] = useState({ width: 0, height: 0 });
  const [image, setImage] = useState({ width: 0, height: 0 });
  const [zoom, setZoom] = useState(1);
  const [pan, setPan] = useState({ x: 0, y: 0 });
  const [dragging, setDragging] = useState(false);

  useEffect(() => {
    dragRef.current = null;
    setDragging(false);
    setZoom(1);
    setPan({ x: 0, y: 0 });
    setImage({ width: 0, height: 0 });
  }, [src]);

  useEffect(() => {
    const node = viewportRef.current;
    if (!node) return undefined;
    const measure = () => {
      setViewport({ width: node.clientWidth, height: node.clientHeight });
    };
    measure();
    if (typeof ResizeObserver === 'undefined') {
      window.addEventListener('resize', measure);
      return () => window.removeEventListener('resize', measure);
    }
    const observer = new ResizeObserver(measure);
    observer.observe(node);
    return () => observer.disconnect();
  }, []);

  const scaleToFit = image.width && image.height && viewport.width && viewport.height
    ? Math.min(viewport.width / image.width, viewport.height / image.height, 1)
    : 1;
  const fittedWidth = image.width * scaleToFit;
  const fittedHeight = image.height * scaleToFit;

  const boundedPan = (x, y, nextZoom = zoom) => {
    const horizontalLimit = Math.max(0, (fittedWidth * nextZoom - viewport.width) / 2);
    const verticalLimit = Math.max(0, (fittedHeight * nextZoom - viewport.height) / 2);
    return {
      x: Math.max(-horizontalLimit, Math.min(horizontalLimit, x)),
      y: Math.max(-verticalLimit, Math.min(verticalLimit, y)),
    };
  };

  useEffect(() => {
    setPan((previous) => {
      const next = boundedPan(previous.x, previous.y);
      return next.x === previous.x && next.y === previous.y ? previous : next;
    });
  }, [zoom, viewport.width, viewport.height, image.width, image.height]);

  const changeZoom = (value) => {
    setZoom((current) => Math.max(1, Math.min(5, typeof value === 'function' ? value(current) : value)));
  };
  const resetZoom = () => {
    setZoom(1);
    setPan({ x: 0, y: 0 });
  };
  const stopDrag = (event) => {
    if (dragRef.current?.pointerId !== event.pointerId) return;
    dragRef.current = null;
    setDragging(false);
    if (event.currentTarget.hasPointerCapture(event.pointerId)) {
      event.currentTarget.releasePointerCapture(event.pointerId);
    }
  };

  const isReady = image.width > 0 && image.height > 0 && viewport.width > 0 && viewport.height > 0;
  return (
    <div className="artwork-viewer">
      {controls && (
        <div className="artwork-viewer__controls" aria-label="Управление просмотром изображения">
          <button type="button" onClick={() => changeZoom((value) => value - 0.5)}
            disabled={zoom <= 1} aria-label="Уменьшить">−</button>
          <span>{Math.round(zoom * 100)}%</span>
          <button type="button" onClick={() => changeZoom((value) => value + 0.5)}
            disabled={zoom >= 5} aria-label="Увеличить">+</button>
          <button type="button" onClick={resetZoom} disabled={zoom === 1}>{t('imageDeck.resetZoom')}</button>
        </div>
      )}
      <div
        ref={viewportRef}
        className={`artwork-viewer__viewport${zoom > 1 ? ' artwork-viewer__viewport--pan' : ''}${dragging ? ' is-dragging' : ''}`}
        onPointerDown={(event) => {
          if (zoom <= 1 || event.button !== 0) return;
          event.preventDefault();
          dragRef.current = {
            pointerId: event.pointerId,
            startX: event.clientX,
            startY: event.clientY,
            originX: pan.x,
            originY: pan.y,
          };
          event.currentTarget.setPointerCapture(event.pointerId);
          setDragging(true);
        }}
        onPointerMove={(event) => {
          const drag = dragRef.current;
          if (!drag || drag.pointerId !== event.pointerId) return;
          event.preventDefault();
          setPan(boundedPan(
            drag.originX + event.clientX - drag.startX,
            drag.originY + event.clientY - drag.startY,
          ));
        }}
        onPointerUp={stopDrag}
        onPointerCancel={stopDrag}
      >
        <img
          className="artwork-viewer__image"
          src={src}
          alt={alt}
          draggable={false}
          onLoad={(event) => {
            setImage({
              width: event.currentTarget.naturalWidth,
              height: event.currentTarget.naturalHeight,
            });
          }}
          style={{
            width: isReady ? fittedWidth : 0,
            height: isReady ? fittedHeight : 0,
            visibility: isReady ? 'visible' : 'hidden',
            transform: `translate(-50%, -50%) translate(${pan.x}px, ${pan.y}px) scale(${zoom})`,
          }}
        />
      </div>
    </div>
  );
}
